/**
 * Administradores de fincas.
 *
 * No son clientes —la factura va siempre a la comunidad— pero son el
 * interlocutor real y comisionan por las comunidades que traen. Existen como
 * ficha propia por una razón muy concreta: escrito a mano en cada comunidad,
 * la misma gestoría acaba con cinco grafías distintas y agruparlas deja de ser
 * posible, que es justo para lo que se quiere esta pantalla.
 *
 * Por eso la cifra que se ve de un vistazo es **cuántas comunidades gestiona
 * cada uno**: es la pregunta que se hace quien entra aquí.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';

interface Administrador {
  id: string;
  code: string;
  name: string;
  tax_id: string | null;
  contact_name: string | null;
  phone: string | null;
  email: string | null;
  commission_rate: string | null;
  is_active: boolean;
  community_count: number;
}

interface Comunidad {
  id: string;
  code: string;
  legal_name: string;
  president_name: string | null;
  maintenance_contact_name: string | null;
  maintenance_contact_phone: string | null;
  site_count: string;
}

const VACIO = {
  code: '',
  name: '',
  taxId: '',
  contactName: '',
  phone: '',
  email: '',
  commissionRate: '',
};

export function PropertyManagersScreen(): React.ReactNode {
  const { api, user } = useSession();
  const puedeCrear = hasPermission(user, 'customers.create');

  const [items, setItems] = useState<Administrador[]>([]);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [comunidades, setComunidades] = useState<Comunidad[]>([]);
  const [alta, setAlta] = useState<typeof VACIO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const datos = await api.get<{ items: Administrador[] }>('/property-managers');
      setItems(datos.items);
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'No se han podido cargar los administradores.',
      );
    }
  }, [api]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function abrir(id: string): Promise<void> {
    if (abierto === id) {
      setAbierto(null);
      return;
    }
    setAbierto(id);
    setComunidades([]);
    try {
      const datos = await api.get<{ items: Comunidad[] }>(`/property-managers/${id}/communities`);
      setComunidades(datos.items);
    } catch {
      setComunidades([]);
    }
  }

  async function guardar(): Promise<void> {
    if (alta === null) return;
    setGuardando(true);
    setError(null);
    try {
      await api.post('/property-managers', {
        code: alta.code.trim(),
        name: alta.name.trim(),
        taxId: alta.taxId.trim() === '' ? null : alta.taxId.trim(),
        contactName: alta.contactName.trim() === '' ? null : alta.contactName.trim(),
        phone: alta.phone.trim() === '' ? null : alta.phone.trim(),
        email: alta.email.trim() === '' ? null : alta.email.trim(),
        commissionRate: alta.commissionRate.trim() === '' ? null : alta.commissionRate.trim(),
      });
      setAlta(null);
      await cargar();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido crear.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <section className="screen">
      <header className="screen__head">
        <h1>Administradores de fincas</h1>
        <p className="screen__note">
          El interlocutor de las comunidades. La factura va siempre a la comunidad; aquí se
          guarda con quién se habla y cuánto comisiona.
        </p>
        {puedeCrear && alta === null && (
          <button
            type="button"
            className="button button--primary button--small"
            onClick={() => setAlta({ ...VACIO })}
          >
            Nuevo administrador
          </button>
        )}
      </header>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}

      {alta !== null && (
        <div className="card form">
          <div className="form__grid">
            <label className="field">
              Código
              <input
                value={alta.code}
                onChange={(e) => setAlta({ ...alta, code: e.target.value })}
                maxLength={32}
              />
            </label>
            <label className="field">
              Nombre
              <input
                value={alta.name}
                onChange={(e) => setAlta({ ...alta, name: e.target.value })}
                maxLength={255}
              />
            </label>
            <label className="field">
              NIF
              <input
                value={alta.taxId}
                onChange={(e) => setAlta({ ...alta, taxId: e.target.value })}
                maxLength={32}
              />
            </label>
            <label className="field">
              Persona de contacto
              <input
                value={alta.contactName}
                onChange={(e) => setAlta({ ...alta, contactName: e.target.value })}
                maxLength={255}
              />
            </label>
            <label className="field">
              Teléfono
              <input
                value={alta.phone}
                onChange={(e) => setAlta({ ...alta, phone: e.target.value })}
                maxLength={32}
              />
            </label>
            <label className="field">
              Correo
              <input
                type="email"
                value={alta.email}
                onChange={(e) => setAlta({ ...alta, email: e.target.value })}
                maxLength={255}
              />
            </label>
            <label className="field">
              Comisión
              <input
                value={alta.commissionRate}
                onChange={(e) => setAlta({ ...alta, commissionRate: e.target.value })}
                placeholder="0.05"
              />
              <span className="field__help">
                Fracción de lo cobrado: 0.05 es el cinco por ciento. Vacío = no comisiona.
              </span>
            </label>
          </div>
          <div className="form__actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => void guardar()}
              disabled={guardando || alta.code.trim() === '' || alta.name.trim() === ''}
            >
              {guardando ? 'Guardando…' : 'Crear'}
            </button>
            <button type="button" className="button button--quiet" onClick={() => setAlta(null)}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {items.length === 0 && <p className="screen__note">Todavía no hay administradores.</p>}

      {items.map((a) => (
        <div key={a.id} className="card">
          <div className="card__label">
            {a.name} <span className="pill">{a.code}</span>
            {a.commission_rate !== null && (
              <span className="pill pill--info">
                comisiona {(Number(a.commission_rate) * 100).toFixed(2)} %
              </span>
            )}
          </div>
          <p className="card__meta">
            {a.contact_name ?? 'Sin persona de contacto'} · {a.phone ?? 'sin teléfono'} ·{' '}
            {a.email ?? 'sin correo'}
          </p>
          <button
            type="button"
            className="button button--quiet button--small"
            onClick={() => void abrir(a.id)}
          >
            {abierto === a.id ? 'Ocultar' : `Ver sus ${a.community_count} comunidades`}
          </button>

          {abierto === a.id && (
            <div className="scroller">
              <table className="table__grid">
                <thead>
                  <tr>
                    <th>Comunidad</th>
                    <th>Presidente</th>
                    <th>Contacto de mantenimiento</th>
                    <th className="table__num">Centros</th>
                  </tr>
                </thead>
                <tbody>
                  {comunidades.map((c) => (
                    <tr key={c.id}>
                      <td>{c.legal_name}</td>
                      <td>{c.president_name ?? '—'}</td>
                      <td>
                        {c.maintenance_contact_name ?? '—'}
                        {c.maintenance_contact_phone !== null && ` · ${c.maintenance_contact_phone}`}
                      </td>
                      <td className="table__num">{c.site_count}</td>
                    </tr>
                  ))}
                  {comunidades.length === 0 && (
                    <tr>
                      <td colSpan={4}>Todavía no gestiona ninguna comunidad.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
